import { Button, Group } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useEffect, useRef } from 'react';
import RerunMenu from '@/components/RerunMenu.tsx';
import { AutoExpandPlan } from '@/types/autoExpandPlan.ts';
import {
  ChildScopeContext,
  getChildScopeActionLabel,
} from '@/pages/Run/components/ScopeStack/childScopeModel.ts';
import { useChildScopeChoices } from '@/pages/Run/components/ScopeStack/useChildScopeChoices.ts';
import { AutomationStep } from '@/types/automationSteps.ts';
import { StepType } from '@/types/automationFields.ts';
import { Run } from '@/types/runs.ts';
import { useIsHistoricalScopePresentation } from '@/pages/Run/components/HistoricalScopePresentationContext.ts';

type Props = {
  run: Run;
  context: ChildScopeContext;
  stepsById: Record<string, AutomationStep>;
  onOpen: (context: ChildScopeContext, isHistorical?: boolean) => void;
  autoExpandPlan?: AutoExpandPlan | null;
};

const rerunMenuStepTypes = [StepType.Iterate, StepType.Repeat, StepType.Retry];

export const ChildScopeContextReporter = ({
  context,
  onContextChange,
}: {
  context: ChildScopeContext;
  onContextChange: (context: ChildScopeContext) => void;
}) => {
  useEffect(() => {
    onContextChange(context);
  }, [context, onContextChange]);

  return null;
};

const ChildScopeLink = ({ run, context, stepsById, onOpen, autoExpandPlan }: Props) => {
  const stepType = context.stepRun.step_type;
  const isHistoricalScopePresentation = useIsHistoricalScopePresentation();
  const appliedAutoExpandPlanRef = useRef<AutoExpandPlan | null>(null);
  const shouldAutoOpen = Boolean(
    autoExpandPlan &&
      (autoExpandPlan.child_scopes_to_open.some(
        (scope) => scope.parent_step_run_id === context.stepRun.id,
      ) ||
        (autoExpandPlan.common_parent_step_run_id === context.stepRun.id &&
          autoExpandPlan.open_common_parent_children)),
  );
  // Retry history stays lazy until it is opened, except when auto-expand targets it.
  // The scope remains openable before historical attempts have loaded.
  const { items } = useChildScopeChoices({
    context,
    stepsById,
    enabled: stepType === StepType.Retry && shouldAutoOpen,
  });
  const canOpen = stepType === StepType.Retry || items.length > 0;

  useEffect(() => {
    if (
      !shouldAutoOpen ||
      !autoExpandPlan ||
      !canOpen ||
      appliedAutoExpandPlanRef.current === autoExpandPlan
    ) {
      return;
    }
    onOpen(context, isHistoricalScopePresentation);
    appliedAutoExpandPlanRef.current = autoExpandPlan;
  }, [
    autoExpandPlan,
    canOpen,
    context,
    isHistoricalScopePresentation,
    onOpen,
    shouldAutoOpen,
  ]);

  return (
    <Group justify="space-between" wrap="nowrap" py="xs">
      <Button
        variant="default"
        color="gray"
        size="compact-sm"
        rightSection={<IconArrowRight size={15} />}
        onClick={() => onOpen(context, isHistoricalScopePresentation)}
        aria-label={getChildScopeActionLabel(stepType)}
        data-scope-open-control-for={context.stepRun.id}
        disabled={!canOpen}
      >
        {getChildScopeActionLabel(stepType)}
      </Button>
      {rerunMenuStepTypes.includes(stepType) && (
        <RerunMenu
          run={run}
          stepRun={context.stepRun}
          step={context.step}
          rerunAllData={{ childrenToRerun: 'all' }}
          rerunFailedData={stepType === StepType.Iterate ? { childrenToRerun: 'failed' } : null}
        />
      )}
    </Group>
  );
};

export default ChildScopeLink;
